/**
 * React Query hooks for the Data Explorer.
 */
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import {
  queryFormView,
  queryRecordView,
  fetchExplorerForms,
  fetchFormCatalog,
  fetchFormCatalogs,
  fetchDataViews,
  createDataView,
  updateDataView,
  softDeleteDataView,
  ensureFieldValueIndex,
  type SaveDataViewInput,
} from './explorer-api'
import type { DataViewSpec } from '../types/data-view-spec'

export function useExplorerFormsQuery(projectId: string | undefined) {
  return useQuery({
    queryKey: ['explorer-forms', projectId],
    queryFn: () => fetchExplorerForms(projectId!),
    enabled: !!projectId,
  })
}

export function useFormCatalogQuery(formId: string | undefined) {
  return useQuery({
    queryKey: ['explorer-form-catalog', formId],
    queryFn: () => fetchFormCatalog(formId!),
    enabled: !!formId,
    staleTime: 5 * 60_000, // form definitions change rarely
  })
}

export function useFormViewQuery(
  spec: DataViewSpec | null,
  page: number,
  pageSize: number,
) {
  const formId = spec?.base.kind === 'form' ? spec.base.formId : ''
  return useQuery({
    queryKey: ['explorer-data', formId, spec, page, pageSize],
    queryFn: () => queryFormView(spec!, page, pageSize),
    enabled: !!spec && !!formId,
    placeholderData: keepPreviousData,
  })
}

export function useFormCatalogsQuery(formIds: string[]) {
  const sorted = [...formIds].sort()
  return useQuery({
    queryKey: ['explorer-form-catalogs', sorted],
    queryFn: () => fetchFormCatalogs(sorted),
    enabled: sorted.length > 0,
    staleTime: 5 * 60_000,
  })
}

export function useRecordViewQuery(
  spec: DataViewSpec | null,
  projectId: string | undefined,
  page: number,
  pageSize: number,
) {
  const enabled = !!spec && spec.base.kind === 'record' && !!projectId
  return useQuery({
    queryKey: ['explorer-record-data', projectId, spec, page, pageSize],
    queryFn: () => queryRecordView(spec!, projectId!, page, pageSize),
    enabled,
    placeholderData: keepPreviousData,
  })
}

export function useDataViewsQuery(projectId: string | undefined) {
  return useQuery({
    queryKey: ['data-views', projectId],
    queryFn: () => fetchDataViews(projectId!),
    enabled: !!projectId,
  })
}

export function useCreateDataViewMutation(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: SaveDataViewInput) => createDataView(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['data-views', projectId] }),
  })
}

export function useUpdateDataViewMutation(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, userId, patch }: { id: string; userId: string; patch: Parameters<typeof updateDataView>[2] }) =>
      updateDataView(id, userId, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['data-views', projectId] }),
  })
}

export function useDeleteDataViewMutation(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) => softDeleteDataView(id, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['data-views', projectId] }),
  })
}

export function useEnsureFieldIndexMutation() {
  return useMutation({
    mutationFn: ({ formId, fieldKey }: { formId: string; fieldKey: string }) =>
      ensureFieldValueIndex(formId, fieldKey),
  })
}
